import { AppWindow } from "lucide-react";
import type { ReactNode } from "react";
import {
	siAnthropic,
	siCursor,
	siGmail,
	siGooglecalendar,
	siGooglechrome,
	siGooglegemini,
} from "simple-icons";
import { LobuMark } from "@/components/brand/lobu-mark";

type AppBrand =
	| "anthropic"
	| "chrome"
	| "cursor"
	| "gemini"
	| "gmail"
	| "google-calendar"
	| "openai";

function brandIcon(
	brand: AppBrand,
	path: string,
	viewBox = "0 0 24 24",
): ReactNode {
	return (
		<svg
			aria-hidden="true"
			className="h-4 w-4 shrink-0"
			data-app-icon={brand}
			viewBox={viewBox}
			fill="currentColor"
		>
			<path d={path} />
		</svg>
	);
}

// Share the installed icon library between MCP clients and connectors.
// OpenAI is retained locally because the installed Simple Icons package omits it.
const OPENAI = brandIcon(
	"openai",
	"M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z",
);
const ANTHROPIC = brandIcon("anthropic", siAnthropic.path);
const GEMINI = brandIcon("gemini", siGooglegemini.path);
const CURSOR = brandIcon("cursor", siCursor.path);
const CHROME = brandIcon("chrome", siGooglechrome.path);
const GMAIL = brandIcon("gmail", siGmail.path);
const GOOGLE_CALENDAR = brandIcon("google-calendar", siGooglecalendar.path);

/** Resolve a brand glyph from a catalog name or an MCP client's reported title. */
export function getAppIcon(name?: string | null): ReactNode | undefined {
	const lower = name?.trim().toLowerCase();
	if (!lower) return undefined;
	if (lower.includes("claude") || lower.includes("anthropic")) return ANTHROPIC;
	if (
		lower.includes("codex") ||
		lower.includes("chatgpt") ||
		lower.includes("openai")
	) {
		return OPENAI;
	}
	if (lower.includes("gemini")) return GEMINI;
	if (lower.includes("cursor")) return CURSOR;
	if (lower.includes("chrome")) return CHROME;
	if (lower.includes("gmail")) return GMAIL;
	if (lower.includes("google calendar") || lower.includes("google.calendar")) {
		return GOOGLE_CALENDAR;
	}
	if (lower.includes("lobu")) return <LobuMark className="h-4 w-4 shrink-0" />;
	return undefined;
}

/** One recognizable MCP-client mark, with a generic fallback for custom apps. */
export function ClientAppIcon({ name }: { name?: string | null }) {
	return getAppIcon(name) ?? <AppWindow className="h-4 w-4 shrink-0" />;
}
